import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { TrendLine } from '../components/charts.tsx';
import { Icon } from '../components/Icon.tsx';
import { CountUp, Reveal } from '../components/motion.tsx';
import { OfficeExport } from '../components/OfficeExport.tsx';
import { Button, Callout, Dialog, Empty, Field, PageHead, Panel, Skeleton, StatusChip, useToast } from '../components/ui.tsx';
import { ApiError, get, post, qs } from '../lib/api.ts';
import { addDays, date, dateRange, idr, idrCompact, todayIn } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';
import { NewThrDialog, type ThrRow } from './ThrRun.tsx';

export interface RunRow {
  id: string;
  office: string;
  officeId: string;
  periodFrom: string;
  periodTo: string;
  transferDate: string | null;
  status: string;
  headcount: number;
  totalNet: string;
  totalTax: string;
  issueCount: number;
  calculatedAt: string | null;
  approvedAt: string | null;
}

/** Legacy cadence: period runs from the 26th to the 25th. */
export function nextPeriod(today: string) {
  const day = Number(today.slice(8, 10));
  const base = day > 25 ? today.slice(0, 8) : addDays(today.slice(0, 8) + '01', -1).slice(0, 8);
  const from = `${base}26`;
  const to = addDays(addDays(`${from.slice(0, 8)}28`, 4).slice(0, 8) + '01', 24);
  return { from, to };
}

export function Payroll() {
  const me = useMe().data!;
  const runs = useQuery({ queryKey: ['runs'], queryFn: () => get<RunRow[]>('/api/payroll/runs') });
  const stats = useQuery({ queryKey: ['payroll-stats'], queryFn: () => get<{ series: { periodTo: string; net: string; tax: string; headcount: number }[] }>('/api/payroll/stats') });
  const [open, setOpen] = useState(false);
  const [thrOpen, setThrOpen] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);
  const bonus = useQuery({ queryKey: ['bonus-batches'], queryFn: () => get<{ id: string; office: string; description: string | null; periodFrom: string; periodTo: string; count: number; total: string; failedRows: { employeeNo: string; reason: string }[] | null }[]>('/api/payroll/bonus') });
  const thr = useQuery({ queryKey: ['thr-runs'], queryFn: () => get<ThrRow[]>('/api/payroll/thr') });
  const latest = useMemo(() => runs.data?.find((r) => r.status === 'approved' || r.status === 'paid'), [runs.data]);
  const latestTotals = useMemo(() => {
    if (!runs.data || !latest) return null;
    const same = runs.data.filter((r) => r.periodTo === latest.periodTo && (r.status === 'approved' || r.status === 'paid'));
    return { net: same.reduce((n, r) => n + Number(r.totalNet), 0), tax: same.reduce((n, r) => n + Number(r.totalTax), 0), headcount: same.reduce((n, r) => n + r.headcount, 0) };
  }, [runs.data, latest]);
  const net = idrCompact(latestTotals?.net);
  const tax = idrCompact(latestTotals?.tax);

  return (
    <div>
      <PageHead
        eyebrow="Payroll"
        title={
          <>
            Pay people <em>right,</em> every time.
          </>
        }
        lede="Runs are calculated by the same engine the old system used, with every known defect behind a named switch. Nothing is paid until a second person approves."
        aside={
          <>
            <OfficeExport label="Salaries" title="Salary sheet" note="Each current employee's basic salary and own grants and deductions, as the old salary download had them." href={(officeId) => `/api/payroll/salaries.xlsx${qs({ officeId })}`} />
            {me.access.payrollRun ? (
              <Button variant="primary" icon="plus" onClick={() => setOpen(true)}>
                New run
              </Button>
            ) : null}
          </>
        }
      />
      <Reveal>
        <Reveal.Item className="grid-12">
          <div className="col-8">
            <div className="ledger" style={{ ['--cols' as string]: 3 }}>
              <div className="ledger__cell ledger__cell--accent">
                <div className="ledger__value">
                  {latestTotals ? <CountUp value={Number(net.value.replace(',', '.'))} format={(n) => n.toLocaleString('id-ID', { maximumFractionDigits: 1 })} /> : '—'}
                  <small>{net.unit}</small>
                </div>
                <div className="ledger__label eyebrow">Net pay · last approved</div>
                <div className="ledger__delta">{latest ? dateRange(latest.periodFrom, latest.periodTo) : ''}</div>
              </div>
              <div className="ledger__cell">
                <div className="ledger__value">
                  {latestTotals ? tax.value : '—'}
                  <small>{tax.unit}</small>
                </div>
                <div className="ledger__label eyebrow">PPh 21 withheld</div>
              </div>
              <div className="ledger__cell">
                <div className="ledger__value">{latestTotals ? <CountUp value={latestTotals.headcount} /> : '—'}</div>
                <div className="ledger__label eyebrow">People paid</div>
              </div>
            </div>
          </div>
          <div className="col-4">
            <span className="eyebrow">Net pay trend</span>
            {stats.data?.series.length && stats.data.series.length > 1 ? <TrendLine points={stats.data.series.map((p) => ({ label: p.periodTo, value: Number(p.net) }))} /> : <p className="faint" style={{ marginTop: 18, fontSize: 'var(--step--1)' }}>The trend appears after two periods.</p>}
          </div>
        </Reveal.Item>
        <Reveal.Item style={{ marginTop: 'var(--s-7)' }}>
          <Panel eyebrow="Runs" title="Recent periods">
            {runs.isLoading ? (
              <Skeleton h={200} />
            ) : !runs.data?.length ? (
              <Empty title="No runs yet." action={me.access.payrollRun ? <Button onClick={() => setOpen(true)}>Start the first run</Button> : undefined} />
            ) : (
              <div>
                {runs.data.map((r) => (
                  <Link key={r.id} to="/payroll/runs/$id" params={{ id: r.id }} className="run-card">
                    <div>
                      <div className="run-card__period">{dateRange(r.periodFrom, r.periodTo)}</div>
                      <div className="eyebrow" style={{ marginTop: 6 }}>
                        {r.office}
                      </div>
                    </div>
                    <div className="row row--wrap" style={{ ['--gap' as string]: '20px', fontSize: 'var(--step--1)' }}>
                      <span>
                        <span className="faint">People</span> <span className="num">{r.headcount}</span>
                      </span>
                      <span>
                        <span className="faint">Net</span> <span className="num">{idr(r.totalNet)}</span>
                      </span>
                      <span>
                        <span className="faint">Tax</span> <span className="num">{idr(r.totalTax)}</span>
                      </span>
                      {r.issueCount ? (
                        <span style={{ color: 'var(--ochre)' }}>
                          <Icon name="alert" width={14} height={14} style={{ display: 'inline', verticalAlign: '-2px' }} /> {r.issueCount} notes
                        </span>
                      ) : null}
                    </div>
                    <StatusChip status={r.status} />
                    <Icon name="chevronRight" width={18} height={18} className="faint" />
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </Reveal.Item>
        <Reveal.Item style={{ marginTop: 'var(--s-6)' }}>
          <Panel eyebrow="Holiday allowance" title="THR runs" action={me.access.payrollRun ? <Button size="sm" icon="plus" onClick={() => setThrOpen(true)}>New THR run</Button> : undefined}>
            {thr.isLoading ? (
              <Skeleton h={120} />
            ) : !thr.data?.length ? (
              <p className="muted" style={{ fontSize: 'var(--step--1)' }}>No THR runs yet. One run per office and feast day, before the holiday.</p>
            ) : (
              <div>
                {thr.data.map((r) => (
                  <Link key={r.id} to="/payroll/thr/$id" params={{ id: r.id }} className="run-card">
                    <div>
                      <div className="run-card__period">{r.feast} {r.year}</div>
                      <div className="eyebrow" style={{ marginTop: 6 }}>{r.office} · transfer {date(r.transferDate)}</div>
                    </div>
                    <div className="row row--wrap" style={{ ['--gap' as string]: '20px', fontSize: 'var(--step--1)' }}>
                      <span><span className="faint">People</span> <span className="num">{r.headcount}</span></span>
                      <span><span className="faint">Total</span> <span className="num">{idr(r.total)}</span></span>
                    </div>
                    <StatusChip status={r.status} />
                    <Icon name="chevronRight" width={18} height={18} className="faint" />
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </Reveal.Item>
        <Reveal.Item style={{ marginTop: 'var(--s-6)' }}>
          <Panel eyebrow="15th-month performance bonus" title="Bonus uploads" action={me.access.payrollRun ? <Button size="sm" icon="plus" onClick={() => setBonusOpen(true)}>Upload</Button> : undefined}>
            {!bonus.data?.length ? (
              <p className="muted" style={{ fontSize: 'var(--step--1)' }}>Upload bonuses for a pay period; they are taxed and paid with that period’s run.</p>
            ) : (
              <ul className="list-plain">
                {bonus.data.map((b) => (
                  <li key={b.id} className="mini-row">
                    <span className="mono faint">{dateRange(b.periodFrom, b.periodTo)}</span>
                    <span>{b.description ?? 'Bonus'} <span className="faint">· {b.office}</span></span>
                    <span className="mini-row__aside num">{b.count} people · {idr(b.total)}{b.failedRows?.length ? <span style={{ color: 'var(--ochre)' }}> · {b.failedRows.length} rejected</span> : null}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </Reveal.Item>
      </Reveal>
      <NewRunDialog open={open} onClose={() => setOpen(false)} />
      {bonusOpen ? <BonusDialog onClose={() => setBonusOpen(false)} /> : null}
      {me.access.payroll ? <TaxYear canExport={me.access.payrollApprove && me.access.sensitive} /> : null}
      <NewThrDialog open={thrOpen} onClose={() => setThrOpen(false)} />
    </div>
  );
}

function NewRunDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const lookups = useLookups();
  const me = useMe().data!;
  const navigate = useNavigate();
  const toast = useToast();
  const p = nextPeriod(todayIn(me.employee?.timeZone ?? undefined));
  const [f, setF] = useState({ officeId: me.employee?.officeId ?? '', periodFrom: p.from, periodTo: p.to, transferDate: addDays(p.to, 3) });
  const create = useMutation({
    mutationFn: async () => {
      const run = await post<{ id: string }>('/api/payroll/runs', { ...f, officeId: f.officeId || lookups.data?.offices[0]?.id });
      await post(`/api/payroll/runs/${run.id}/calculate`);
      return run;
    },
    onSuccess: (run) => {
      toast({ title: 'Run calculated', body: 'Review the figures, then ask a colleague to approve.' });
      onClose();
      navigate({ to: '/payroll/runs/$id', params: { id: run.id } });
    },
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="New payroll run"
      title={
        <>
          Which <em className="serif-italic">period?</em>
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
            Create and calculate
          </Button>
        </>
      }
    >
      <div className="grid-12" style={{ gap: 14 }}>
        <div className="col-12">
          <Field label="Office">
            {(pp) => (
              <select {...pp} className="select" value={f.officeId} onChange={(e) => setF({ ...f, officeId: e.target.value })}>
                {lookups.data?.offices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        <div className="col-6">
          <Field label="Period from">{(pp) => <input {...pp} type="date" className="input" value={f.periodFrom} onChange={(e) => setF({ ...f, periodFrom: e.target.value })} />}</Field>
        </div>
        <div className="col-6">
          <Field label="Period to">{(pp) => <input {...pp} type="date" className="input" value={f.periodTo} onChange={(e) => setF({ ...f, periodTo: e.target.value })} />}</Field>
        </div>
        <div className="col-6">
          <Field label="Transfer date">{(pp) => <input {...pp} type="date" className="input" value={f.transferDate} onChange={(e) => setF({ ...f, transferDate: e.target.value })} />}</Field>
        </div>
        <div className="col-12">
          <Callout tone="info">Timesheets for the period are re-derived from punches before calculating. {date(f.periodFrom)} – {date(f.periodTo)}.</Callout>
        </div>
        {create.error ? (
          <div className="col-12">
            <Callout tone="bad">{create.error instanceof ApiError ? create.error.message : 'Could not create the run.'}</Callout>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Rows "employee number, amount" — CSV or pasted straight from a spreadsheet. */
function parseBonusRows(text: string) {
  return text
    .split(/\r?\n/)
    .map((l) => l.split(/[\t,;]/).map((c) => c.trim()))
    .filter((c) => c.length >= 2 && /\d/.test(c[1] ?? '') && !/[a-z]/i.test(c[1]!))
    .map((c) => ({ employeeNo: c[0]!.replace(/\.0$/, ''), amount: normaliseAmount(c[1]!) }));
}

/** "3.500.000", "3,500,000", "Rp 3500000", "1250000,50" → "3500000" / "1250000.50". */
export function normaliseAmount(raw: string): string {
  const v = raw.replace(/rp/i, '').replace(/\s/g, '');
  if (/^\d{1,3}([.,]\d{3})+$/.test(v)) return v.replace(/[.,]/g, '');
  if (/^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(v)) return v.replace(/\./g, '').replace(',', '.');
  if (/^\d+([.,]\d{1,2})?$/.test(v)) return v.replace(',', '.');
  return v.replace(/\D/g, '');
}

function BonusDialog({ onClose }: { onClose: () => void }) {
  const lookups = useLookups();
  const me = useMe().data!;
  const toast = useToast();
  const qc = useQueryClient();
  const p = nextPeriod(todayIn(me.employee?.timeZone ?? undefined));
  const [f, setF] = useState({ officeId: me.employee?.officeId ?? '', periodFrom: p.from, periodTo: p.to, description: '', text: '' });
  const rows = parseBonusRows(f.text);
  const m = useMutation({
    mutationFn: () => post<{ imported: number; failed: { employeeNo: string; reason: string }[] }>('/api/payroll/bonus', { officeId: f.officeId || lookups.data?.offices[0]?.id, periodFrom: f.periodFrom, periodTo: f.periodTo, description: f.description || undefined, rows }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['bonus-batches'] });
      toast({ title: `${r.imported} bonus${r.imported === 1 ? '' : 'es'} imported`, body: r.failed.length ? `${r.failed.length} rows rejected (unknown employee number)` : undefined });
      onClose();
    },
  });
  const onFile = async (file: File | undefined) => {
    if (file) setF((x) => ({ ...x, text: '' }));
    if (file) {
      const t = await file.text();
      setF((x) => ({ ...x, text: t }));
    }
  };
  return (
    <Dialog open onClose={onClose} eyebrow="15th-month bonus" title="Upload bonuses" width={620} footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!rows.length} loading={m.isPending} onClick={() => m.mutate()}>Import {rows.length || ''}</Button></>}>
      <div className="grid-12" style={{ gap: 14 }}>
        <div className="col-6"><Field label="Office">{(a) => <select {...a} className="select" value={f.officeId} onChange={(e) => setF({ ...f, officeId: e.target.value })}>{lookups.data?.offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}</Field></div>
        <div className="col-6"><Field label="Description">{(a) => <input {...a} className="input" value={f.description} placeholder="2026 performance" onChange={(e) => setF({ ...f, description: e.target.value })} />}</Field></div>
        <div className="col-6"><Field label="Pay period from">{(a) => <input {...a} type="date" className="input" value={f.periodFrom} onChange={(e) => setF({ ...f, periodFrom: e.target.value })} />}</Field></div>
        <div className="col-6"><Field label="to">{(a) => <input {...a} type="date" className="input" value={f.periodTo} onChange={(e) => setF({ ...f, periodTo: e.target.value })} />}</Field></div>
        <div className="col-12"><Field label="Rows" hint="Employee number and amount per line — paste from a spreadsheet or choose a CSV file">{(a) => <textarea {...a} className="input input--mono" rows={7} value={f.text} placeholder={'12001518\t3500000\n12002110\t2750000'} onChange={(e) => setF({ ...f, text: e.target.value })} />}</Field></div>
        <div className="col-12 row row--between">
          <input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => void onFile(e.target.files?.[0])} aria-label="CSV file" />
          <span className="faint mono" style={{ fontSize: 11 }}>{rows.length} rows · {idr(rows.reduce((n, r) => n + Number(r.amount || 0), 0).toFixed(2))}</span>
        </div>
        {m.error ? <div className="col-12"><Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Upload failed'}</Callout></div> : null}
      </div>
    </Dialog>
  );
}

interface TaxYearData {
  year: number;
  rows: { employeeId: string; employeeNo: string | null; name: string; months: number; bruto: string; pkp: string; taxDue: string; withheld: string; difference: string }[];
  totals: { bruto: string; taxDue: string; withheld: string; difference: string };
}

/** Year-end PPh 21: what form 1721-A1 will show, and the December true-up per person. */
function TaxYear({ canExport }: { canExport: boolean }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [all, setAll] = useState(false);
  const q = useQuery({ queryKey: ['tax-year', year], queryFn: () => get<TaxYearData>(`/api/payroll/tax-year?year=${year}`) });
  const toast = useToast();
  const exportM = useMutation({ mutationFn: () => post<{ fileId: string }>('/api/payroll/tax-year/export', { year }), onSuccess: (r) => window.location.assign(`/api/payroll/files/${r.fileId}`), onError: (e) => toast({ title: e instanceof ApiError ? e.message : 'Export failed', tone: 'error' }) });
  const rows = q.data?.rows ?? [];
  const off = rows.filter((r) => Math.abs(Number(r.difference)) >= 1);
  return (
    <section style={{ marginTop: 'var(--s-6)' }}>
      <Panel
        eyebrow="Year-end PPh 21 · form 1721-A1"
        title={`Tax year ${year}`}
        action={
          <div className="row" style={{ gap: 8 }}>
            <select className="select select--sm" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Tax year">
              {[thisYear, thisYear - 1, thisYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            {canExport ? <Button size="sm" icon="download" loading={exportM.isPending} onClick={() => exportM.mutate()}>Export (NIK/NPWP)</Button> : null}
          </div>
        }
      >
        {q.isLoading ? (
          <Skeleton h={120} />
        ) : !rows.length ? (
          <p className="muted" style={{ fontSize: 'var(--step--1)' }}>No approved payroll in {year} yet.</p>
        ) : (
          <div className="stack">
            <div className="row row--wrap" style={{ ['--gap' as string]: '28px', fontSize: 'var(--step--1)' }}>
              <span><span className="faint">People</span> <span className="num">{rows.length}</span></span>
              <span><span className="faint">Gross</span> <span className="num">{idr(q.data!.totals.bruto)}</span></span>
              <span><span className="faint">PPh due</span> <span className="num">{idr(q.data!.totals.taxDue)}</span></span>
              <span><span className="faint">Withheld</span> <span className="num">{idr(q.data!.totals.withheld)}</span></span>
              <span style={{ color: Math.abs(Number(q.data!.totals.difference)) >= 1 ? 'var(--ochre)' : undefined }}><span className="faint">True-up</span> <span className="num">{idr(q.data!.totals.difference)}</span></span>
            </div>
            <p className="faint" style={{ fontSize: 'var(--step--1)' }}>{off.length ? `${off.length} people need a December true-up: the year’s tax on annual PKP differs from what was withheld monthly.` : 'Withholding matches the annual tax for everyone.'}</p>
            <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th className="right hide-sm">Months</th>
                  <th className="right">Gross</th>
                  <th className="right hide-sm">PKP</th>
                  <th className="right">Due</th>
                  <th className="right">Withheld</th>
                  <th className="right">Δ</th>
                </tr>
              </thead>
              <tbody>
                {(all ? rows : (off.length ? off : rows).slice(0, 12)).map((r) => (
                  <tr key={r.employeeId}>
                    <td>{r.name}<div className="faint mono" style={{ fontSize: 10.5 }}>{r.employeeNo}</div></td>
                    <td className="right num hide-sm">{r.months}</td>
                    <td className="right num nowrap">{idr(r.bruto)}</td>
                    <td className="right num nowrap hide-sm">{idr(r.pkp)}</td>
                    <td className="right num nowrap">{idr(r.taxDue)}</td>
                    <td className="right num nowrap">{idr(r.withheld)}</td>
                    <td className="right num nowrap" style={{ color: Math.abs(Number(r.difference)) >= 1 ? 'var(--ochre)' : 'var(--ink-3)' }}>{idr(r.difference)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {!all && rows.length > 12 ? <Button size="sm" variant="ghost" onClick={() => setAll(true)}>Show all {rows.length}</Button> : null}
          </div>
        )}
      </Panel>
    </section>
  );
}
